import { createEdge, createNode } from '../../core/defaults';
import type { DiagramEdge, DiagramNode, NodeType } from '../../core/types';
import { parseCsv } from './orgCsv';

export interface NetworkCsvResult {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  warnings: string[];
  rowCount: number;
}

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
}

function findColumn(headers: string[], aliases: string[]): number {
  const normalizedHeaders = headers.map(normalized);
  return normalizedHeaders.findIndex((header) => aliases.includes(header));
}

function typeFrom(value: string): NodeType | null {
  const key = normalized(value);
  if (['servidor', 'server', 'vm', 'host'].includes(key)) return 'networkServer';
  if (['switch', 'sw'].includes(key)) return 'networkSwitch';
  if (['roteador', 'router', 'gateway', 'rt'].includes(key)) return 'networkRouter';
  if (['firewall', 'fw', 'seguranca', 'security'].includes(key)) return 'networkFirewall';
  if (['estacao', 'cliente', 'client', 'pc', 'desktop', 'notebook', 'workstation'].includes(key)) return 'networkClient';
  if (['nuvem', 'internet', 'cloud', 'wan'].includes(key)) return 'networkCloud';
  return null;
}

function normalizeStatus(value: string): string {
  const key = normalized(value);
  if (['online', 'ativo', 'active', 'up'].includes(key)) return 'online';
  if (['offline', 'inativo', 'inactive', 'down'].includes(key)) return 'offline';
  if (['manutencao', 'maintenance'].includes(key)) return 'maintenance';
  if (['planejado', 'planned'].includes(key)) return 'planned';
  return 'unknown';
}

export function importNetworkCsv(text: string): NetworkCsvResult {
  if (text.length > 5_000_000) throw new Error('O CSV de rede excede o limite local de 5 MB.');
  const rows = parseCsv(text.replace(/^\uFEFF/, ''));
  if (rows.length < 2) throw new Error('O CSV de rede precisa conter cabeçalho e pelo menos uma linha de dados.');

  const headers = rows[0];
  const typeIndex = findColumn(headers, ['tipo', 'type', 'dispositivo', 'device', 'categoria']);
  const hostIndex = findColumn(headers, ['hostname', 'host', 'nome', 'name', 'identificacao', 'equipamento']);
  const ipIndex = findColumn(headers, ['ip', 'enderecoip', 'ipaddress']);
  const vlanIndex = findColumn(headers, ['vlan', 'rede', 'network']);
  const statusIndex = findColumn(headers, ['status', 'estado', 'state']);
  const locationIndex = findColumn(headers, ['localizacao', 'location', 'local', 'rack', 'sala']);
  const upstreamIndex = findColumn(headers, ['conectadoa', 'connectedto', 'uplink', 'upstream', 'pai', 'parent']);
  if (typeIndex < 0) throw new Error('Não encontrei a coluna “Tipo” no CSV de rede.');
  if (hostIndex < 0) throw new Error('Não encontrei a coluna “Hostname” ou “Nome” no CSV de rede.');

  const warnings: string[] = [];
  const entries = rows.slice(1, 3001).map((columns, index) => ({
    line: index + 2,
    typeRaw: (columns[typeIndex] ?? '').trim(),
    hostname: (columns[hostIndex] ?? '').trim(),
    ip: ipIndex >= 0 ? (columns[ipIndex] ?? '').trim() : '',
    vlan: vlanIndex >= 0 ? (columns[vlanIndex] ?? '').trim() : '',
    status: statusIndex >= 0 ? (columns[statusIndex] ?? '').trim() : '',
    location: locationIndex >= 0 ? (columns[locationIndex] ?? '').trim() : '',
    upstream: upstreamIndex >= 0 ? (columns[upstreamIndex] ?? '').trim() : '',
  })).filter((row) => row.hostname || row.typeRaw);

  if (rows.length - 1 > 3000) warnings.push('A importação foi limitada aos primeiros 3.000 equipamentos para preservar a responsividade do navegador.');
  if (entries.length === 0) throw new Error('Nenhum equipamento foi encontrado no CSV.');

  const nodes: DiagramNode[] = [];
  const nodeByHost = new Map<string, DiagramNode>();
  const sourceByNodeId = new Map<string, (typeof entries)[number]>();
  for (const row of entries) {
    const type = typeFrom(row.typeRaw);
    if (!type) { warnings.push(`Linha ${row.line}: tipo “${row.typeRaw || '(vazio)'}” não reconhecido; linha ignorada.`); continue; }
    const hostname = row.hostname || `Equipamento ${row.line}`;
    const key = normalized(hostname);
    if (nodeByHost.has(key)) warnings.push(`Linha ${row.line}: hostname duplicado “${hostname}”; a primeira ocorrência será usada para conexões.`);
    const node = createNode(type);
    node.text = hostname;
    node.data = {
      ...node.data,
      hostname,
      ip: row.ip,
      vlan: row.vlan,
      status: normalizeStatus(row.status),
      location: row.location,
    };
    nodes.push(node);
    sourceByNodeId.set(node.id, row);
    if (!nodeByHost.has(key)) nodeByHost.set(key, node);
  }
  if (nodes.length === 0) throw new Error('Nenhum tipo de equipamento suportado foi encontrado no CSV.');

  const edges: DiagramEdge[] = [];
  for (const node of nodes) {
    const row = sourceByNodeId.get(node.id);
    if (!row?.upstream) continue;
    const parent = nodeByHost.get(normalized(row.upstream));
    if (!parent) { warnings.push(`Linha ${row.line}: equipamento conectado “${row.upstream}” não foi encontrado.`); continue; }
    if (parent.id === node.id) { warnings.push(`Linha ${row.line}: um equipamento não pode estar conectado a si mesmo.`); continue; }
    const edge = createEdge(parent.id, node.id);
    edge.arrowEnd = false;
    edge.label = row.vlan ? `VLAN ${row.vlan}` : '';
    edges.push(edge);
  }

  return { nodes, edges, warnings: Array.from(new Set(warnings)).slice(0, 100), rowCount: nodes.length };
}
